import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listDisputes } from "@/lib/disputes.functions";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { Badge } from "@/components/ui/badge";
import { formatGBP } from "@/lib/format";
import { AlertOctagon } from "lucide-react";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/_authenticated/disputes")({
  head: () => ({ meta: [{ title: "Disputes — BidNova Auctions" }] }),
  component: DisputesPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

const STATUS_LABELS: Record<string, string> = {
  open: "Open",
  awaiting_seller: "Awaiting seller",
  awaiting_buyer: "Awaiting buyer",
  under_review: "Under review",
  resolved_buyer: "Resolved (buyer)",
  resolved_seller: "Resolved (seller)",
  closed: "Closed",
};

const STATUS_KEY_PREFIX = "disputeStatus";
const STATUS_TONE: Record<string, string> = {
  open: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  awaiting_seller: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  awaiting_buyer: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  under_review: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  resolved_buyer: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  resolved_seller: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  closed: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

function DisputesPage() {
  const { t } = useTranslation("commerce");
  const listFn = useServerFn(listDisputes);
  const { data, isLoading, error } = useQuery({
    queryKey: ["disputes", "list"],
    queryFn: () => listFn(),
  });

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-gold">{t("disputesList.eyebrow", "Dispute Centre")}</p>
        <h1 className="mt-2 font-display text-4xl">{t("disputesList.title", "Your disputes")}</h1>
        <p className="mt-1 text-muted-foreground">{t("disputesList.subtitle", "Track and respond to order disputes.")}</p>
      </div>
      {error && <div className="text-sm text-destructive">{t("disputesList.loadError", "Could not load disputes: {message}", { message: (error as Error).message })}</div>}
      {isLoading ? (
        <p className="text-muted-foreground">{t("disputesList.loading", "Loading…")}</p>
      ) : (data?.disputes ?? []).length === 0 ? (
        <div className="rounded-xl border border-border/60 p-10 text-center text-muted-foreground">
          <AlertOctagon className="h-8 w-8 mx-auto mb-2 opacity-50" />
          {t("disputesList.empty", "No disputes yet.")}
        </div>
      ) : (
        <ul className="space-y-3">
          {(data!.disputes as Array<{
            id: string; order_id: string; status: string; reason: string; requested_resolution: string;
            created_at: string;
            orders: { amount_pence: number; auctions: { title: string; images: string[] | null } | null } | null;
          }>).map((d) => (
            <li key={d.id}>
              <Link
                to="/disputes/$disputeId"
                params={{ disputeId: d.id }}
                className="flex items-center gap-4 rounded-lg border border-border/60 bg-surface-2/40 px-4 py-3 hover:bg-surface-2 transition-colors"
              >
                {d.orders?.auctions?.images?.[0] ? (
                  <img src={d.orders.auctions.images[0]} alt="" className="h-12 w-12 rounded object-cover" />
                ) : <div className="h-12 w-12 rounded bg-surface-1" />}
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{d.orders?.auctions?.title ?? t("disputesList.orderFallback", "Order")}</p>
                  <p className="text-xs text-muted-foreground font-mono">{t("disputesList.openedOn", "#{id} · Opened {date}", { id: d.id.slice(0, 8), date: new Date(d.created_at).toLocaleDateString("en-GB") })}</p>
                </div>
                <Badge variant="outline" className={STATUS_TONE[d.status] ?? ""}>{t(`${STATUS_KEY_PREFIX}.${d.status}`, STATUS_LABELS[d.status] ?? d.status)}</Badge>
                <div className="text-right hidden sm:block">
                  <div className="font-display text-gold-gradient">{formatGBP(d.orders?.amount_pence ?? 0)}</div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
